/**
 * eSalon Custom Consultation (DEV-4285)
 *
 * Drives the consultation quiz rendered by sections/esalon-custom-consultation.liquid:
 * selection state, progress, step navigation, and the name-on-product overlay.
 *
 * Phase 1 scope. Completing the flow collects the answers and dispatches
 * `consultation:complete`; the Ajax Cart add (Phase 2) and the Recharge
 * subscription path (Phase 3) are both blocked on DEV-4218 and attach here.
 */

class ConsultationForm extends HTMLElement {
  connectedCallback() {
    this.profile = this.dataset.profile || 'consultation';
    this.storageKey = `esalon:consultation:${this.profile}`;
    this.nextUrl = this.dataset.nextUrl || '';

    // This step's slice of the flow-wide progress bar (e.g. 0-33 on step 1 of 3),
    // so the bar keeps climbing across pages instead of restarting each step.
    this.progressMin = Number.parseFloat(this.dataset.progressMin);
    this.progressMax = Number.parseFloat(this.dataset.progressMax);
    if (!Number.isFinite(this.progressMin)) this.progressMin = 0;
    if (!Number.isFinite(this.progressMax)) this.progressMax = 100;

    this.questions = Array.from(this.querySelectorAll('[data-consultation-question]'));
    this.progressBar = this.querySelector('[data-consultation-progress]');
    this.submitButton = this.querySelector('[data-consultation-submit]');
    this.hint = this.querySelector('[data-consultation-hint]');
    this.nextArrow = this.querySelector('[data-consultation-next]');
    this.prevArrow = this.querySelector('[data-consultation-prev]');
    this.bottleName = this.querySelector('[data-consultation-bottle-name]');
    this.nameInput = this.querySelector('[data-consultation-name-input]');

    this.restore();
    this.bindEvents();
    this.update();
  }

  bindEvents() {
    this.addEventListener('change', (event) => {
      if (!event.target.matches('[data-consultation-input]')) return;
      this.enforceExclusive(event.target);
      this.update();
    });

    if (this.nameInput) {
      this.nameInput.addEventListener('input', () => {
        this.sanitizeName();
        this.renderBottleName();
        this.update();
      });
    }

    this.querySelectorAll('[data-consultation-nav-up]').forEach((button) => {
      button.addEventListener('click', () => this.scrollToPrevious(button));
    });

    if (this.submitButton) {
      this.submitButton.addEventListener('click', () => this.submit());
    }

    if (this.nextArrow) {
      this.nextArrow.addEventListener('click', () => {
        this.submit();
      });
    }

    if (this.prevArrow) {
      this.prevArrow.addEventListener('click', () => {
        if (!this.prevArrow.disabled) window.history.back();
      });
    }
  }

  /** Parses a comma-separated question setting into trimmed values. */
  optionList(value) {
    return (value || '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  /**
   * "None of these" cannot coexist with a real answer. Ticking an exclusive
   * option clears the other answers to that question; ticking any other answer
   * clears the exclusive one.
   *
   * `exclusive_compatible` carves out the exceptions, and they are genuinely
   * needed: on Growth, "Hair breakage" is not a form of hair LOSS, so it may sit
   * alongside "I'm not experiencing hair loss" — and because it is compatible,
   * ticking it clears nothing either. The relationship is symmetric.
   *
   * Both lists come from per-question settings rather than a hardcoded `'none'`,
   * because this section is shared with Custom Care and Custom Color, and a
   * question whose "none" is a legitimate co-selection must be left alone.
   *
   * Single-select questions are skipped: the radio group already enforces it.
   */
  enforceExclusive(input) {
    if (!input.checked) return;

    const question = input.closest('[data-consultation-question]');
    if (!question || question.dataset.questionType !== 'multi') return;

    const exclusive = this.optionList(question.dataset.exclusiveValues);
    if (!exclusive.length) return;

    const compatible = this.optionList(question.dataset.exclusiveCompatible);
    const pickedExclusive = exclusive.includes(input.value);

    // A compatible answer coexists with the exclusive option in both
    // directions, so picking it clears nothing at all.
    if (!pickedExclusive && compatible.includes(input.value)) return;

    question.querySelectorAll('[data-consultation-input]').forEach((other) => {
      if (other === input) return;

      if (pickedExclusive) {
        // Clear the other answers, but leave the compatible ones standing.
        if (!compatible.includes(other.value)) other.checked = false;
      } else if (exclusive.includes(other.value)) {
        other.checked = false;
      }
    });
  }

  /**
   * The live widget accepts printable characters only. Native `maxlength`
   * covers the length cap; this strips control characters that survive paste.
   */
  sanitizeName() {
    // Strip C0/C1 control characters. Letters, spaces, hyphens, apostrophes
    // and accented characters are all valid in a name and must survive.
    const cleaned = this.nameInput.value.replace(/\p{Cc}/gu, '');
    if (cleaned === this.nameInput.value) return;
    const caret = this.nameInput.selectionStart;
    this.nameInput.value = cleaned;
    this.nameInput.setSelectionRange(caret, caret);
  }

  renderBottleName() {
    if (!this.bottleName || !this.nameInput) return;
    this.bottleName.textContent = this.nameInput.value.trim();
  }

  /** Collected answers, keyed by each question's answer key. */
  getAnswers() {
    const answers = {};

    this.questions.forEach((question) => {
      const key = question.dataset.questionKey;
      const type = question.dataset.questionType;

      if (type === 'text') {
        const input = question.querySelector('[data-consultation-name-input]');
        const value = input ? input.value.trim() : '';
        if (value) answers[key] = value;
        return;
      }

      const checked = Array.from(question.querySelectorAll('[data-consultation-input]:checked'));
      if (!checked.length) return;

      answers[key] = type === 'multi' ? checked.map((input) => input.value) : checked[0].value;
    });

    return answers;
  }

  isAnswered(question) {
    const key = question.dataset.questionKey;
    const answer = this.getAnswers()[key];
    return Array.isArray(answer) ? answer.length > 0 : Boolean(answer);
  }

  update() {
    const required = this.questions.filter((question) => question.dataset.required === 'true');
    const answeredRequired = required.filter((question) => this.isAnswered(question));
    const complete = answeredRequired.length === required.length;

    // Progress reflects every question, so optional ones still move the bar.
    const answeredAll = this.questions.filter((question) => this.isAnswered(question)).length;
    const span = this.progressMax - this.progressMin;
    const percent = this.questions.length
      ? Math.round(this.progressMin + (answeredAll / this.questions.length) * span)
      : this.progressMin;

    if (this.progressBar) {
      this.progressBar.style.width = `${percent}%`;
      this.progressBar.setAttribute('aria-valuenow', String(percent));
    }

    // `aria-disabled`, NOT `disabled`. A truly disabled button cannot be
    // clicked, so `focusFirstIncomplete()` could never run and the customer was
    // left with a permanently greyed Continue and nothing anywhere on the page
    // saying what was missing (WCAG 3.3.1). Now the press is allowed through,
    // and it scrolls to the first unanswered question and explains itself.
    if (this.submitButton) {
      this.submitButton.setAttribute('aria-disabled', String(!complete));
    }
    if (this.nextArrow) {
      this.nextArrow.setAttribute('aria-disabled', String(!complete));
    }

    if (this.hint && complete) this.hint.textContent = '';

    this.persist();
  }

  scrollToPrevious(button) {
    const current = button.closest('[data-consultation-question]');
    const index = this.questions.indexOf(current);
    const target = this.questions[index - 1];
    if (!target) return;

    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  /** Scrolls to the first unanswered required question and flags it. */
  focusFirstIncomplete() {
    const incomplete = this.questions.find(
      (question) => question.dataset.required === 'true' && !this.isAnswered(question)
    );
    if (!incomplete) return false;

    // Both guarded. This runs on the VALIDATION FAILURE path, so a throw here
    // means clicking Continue on an incomplete quiz does nothing at all — no
    // hint, no scroll, just a console error. Neither scrolling nor the motion
    // query is load-bearing; the hint below is.
    let behavior = 'smooth';
    try {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) behavior = 'auto';
    } catch {
      behavior = 'auto';
    }

    if (typeof incomplete.scrollIntoView === 'function') {
      incomplete.scrollIntoView({ behavior, block: 'center' });
    }

    if (this.hint) this.hint.textContent = 'Please answer every question to continue.';
    return true;
  }

  submit() {
    if (this.focusFirstIncomplete()) return;

    const answers = this.getAnswers();
    this.persist();

    // Only here, after focusFirstIncomplete() has passed — this marker is what
    // steps 2 and 3 gate on, so it must mean "finished", not "started".
    const gate = window.esalonConsultation || {};
    if (gate.markComplete) gate.markComplete(this.profile);

    // Phase 2 (DEV-4285, blocked on DEV-4218) attaches the Ajax Cart add here,
    // using the property shape from buildLineItemProperties().
    const event = new CustomEvent('consultation:complete', {
      bubbles: true,
      cancelable: true,
      detail: {
        profile: this.profile,
        answers,
        properties: this.buildLineItemProperties(answers),
      },
    });
    this.dispatchEvent(event);

    // Answers are already in sessionStorage, so the next step reads them there.
    // Cancelling the event lets a future cart-add handler take over navigation.
    if (!event.defaultPrevented && this.nextUrl) window.location.href = this.nextUrl;
  }

  /**
   * Cart line item properties for the Ajax Cart API.
   *
   * Delegates to the shared builder in esalon-consultation-properties.js — the
   * routine step posts the same shape, and two copies of the key convention
   * would drift silently.
   */
  buildLineItemProperties(answers = this.getAnswers()) {
    const build = (window.esalonConsultation || {}).buildLineItemProperties;
    return build ? build(this.profile, answers) : {};
  }

  persist() {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.getAnswers()));
    } catch {
      // Private browsing or a full quota — the flow still works, it just won't
      // survive a reload. Not worth surfacing to the customer.
    }
  }

  restore() {
    let saved;
    try {
      saved = JSON.parse(sessionStorage.getItem(this.storageKey) || '{}');
    } catch {
      return;
    }

    this.questions.forEach((question) => {
      const key = question.dataset.questionKey;
      const value = saved[key];
      if (value === undefined) return;

      if (question.dataset.questionType === 'text') {
        const input = question.querySelector('[data-consultation-name-input]');
        if (input) input.value = value;
        return;
      }

      const values = Array.isArray(value) ? value : [value];
      question.querySelectorAll('[data-consultation-input]').forEach((input) => {
        if (values.includes(input.value)) input.checked = true;
      });
    });

    this.renderBottleName();
  }
}

if (!customElements.get('consultation-form')) {
  customElements.define('consultation-form', ConsultationForm);
}
